import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Palette, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';
import { EnterpriseProofPack } from '../components/enterprise/EnterpriseProofPack';
import { EnterpriseSalesNav } from '../components/enterprise/EnterpriseSalesNav';
import { useAuth } from '../lib/auth-context';
import { getBrandingProfile, upsertBrandingProfile } from '../lib/enterprise-api';
import { useSEO } from '../lib/seo';
import { EnterpriseOrgSwitcher } from '../components/enterprise/EnterpriseOrgSwitcher';
import { useEnterpriseOrg } from '../context/EnterpriseOrgContext';

export default function EnterpriseWhiteLabel() {
  useSEO({
    title: 'Enterprise White-Label | TENSION for Work',
    description: 'Configure organization-level branding profile for enterprise and partner white-label deployments.',
    canonical: '/enterprise/whitelabel',
    ogType: 'website',
  });
  const { accessToken } = useAuth();
  const { activeOrgId } = useEnterpriseOrg();
  const [orgId, setOrgId] = useState('');
  const [brandName, setBrandName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#6d28d9');
  const [accentColor, setAccentColor] = useState('#d946ef');
  const [customDomain, setCustomDomain] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!orgId && activeOrgId) setOrgId(activeOrgId);
  }, [activeOrgId, orgId]);

  const load = async () => {
    if (!accessToken) return toast.error('Sign in required');
    if (!orgId.trim()) return toast.error('Enter org id');
    setLoading(true);
    const result = await getBrandingProfile(orgId.trim(), accessToken);
    setLoading(false);
    if (!result.success) return toast.error(result.error || 'Failed to load profile');
    const b = (result.branding || {}) as Record<string, unknown>;
    setBrandName(String(b.brand_name || ''));
    setLogoUrl(String(b.logo_url || ''));
    setSupportEmail(String(b.support_email || ''));
    setPrimaryColor(String(b.primary_color || '#6d28d9'));
    setAccentColor(String(b.accent_color || '#d946ef'));
    setCustomDomain(String(b.custom_domain || ''));
    toast.success('Branding loaded');
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!accessToken) return toast.error('Sign in required');
    if (!orgId.trim()) return toast.error('Enter org id');
    setLoading(true);
    const result = await upsertBrandingProfile(
      orgId.trim(),
      { brandName, logoUrl, supportEmail, primaryColor, accentColor, customDomain },
      accessToken,
    );
    setLoading(false);
    if (!result.success) return toast.error(result.error || 'Failed to save branding');
    toast.success('Branding profile saved');
  };

  return (
    <WarmMarketingBackdrop>
      <EnterpriseSalesNav variant="light" />
      <div className="enterprise-public-shell mx-auto min-h-screen max-w-4xl space-y-6 px-4 py-10 md:py-14">
        <header className="enterprise-hero-shell">
          <div className="enterprise-hero-overlay" />
          <div className="enterprise-hero-content">
          <p className="inline-flex rounded-full bg-violet-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-violet-700">
            White-label
          </p>
          <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-900 md:text-5xl">Organization branding profile</h1>
          <p className="mt-3 text-slate-600 md:text-lg">
            Prepare enterprise-facing branding controls for custom domains and partner deployments.
          </p>
          </div>
        </header>

        <Card className="enterprise-surface-card p-6">
          <form className="grid gap-3 md:grid-cols-2" onSubmit={save}>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="org-id">Organization ID</Label>
              <Input id="org-id" value={orgId} onChange={(e) => setOrgId(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="brand-name">Brand name</Label>
              <Input id="brand-name" value={brandName} onChange={(e) => setBrandName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="support-email">Support email</Label>
              <Input id="support-email" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="logo-url">Logo URL</Label>
              <Input id="logo-url" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="primary-color">Primary color</Label>
              <Input id="primary-color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="accent-color">Accent color</Label>
              <Input id="accent-color" value={accentColor} onChange={(e) => setAccentColor(e.target.value)} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="custom-domain">Custom domain</Label>
              <Input id="custom-domain" value={customDomain} onChange={(e) => setCustomDomain(e.target.value)} placeholder="enterprise.example.com" />
            </div>
            <div className="md:col-span-2 flex flex-wrap gap-2">
              <Button type="submit" disabled={loading}>
                Save white-label profile
              </Button>
              <Button type="button" variant="outline" onClick={load} disabled={loading}>
                Load profile
              </Button>
              <Link to="/enterprise/partner-docs">
                <Button type="button" variant="ghost">
                  Partner docs
                </Button>
              </Link>
            </div>
          </form>
        </Card>

        <Card className="enterprise-surface-card p-4">
          <EnterpriseOrgSwitcher />
          {activeOrgId && !orgId && (
            <button
              type="button"
              className="mt-3 text-xs text-violet-700 underline"
              onClick={() => setOrgId(activeOrgId)}
            >
              Use active org id in form
            </button>
          )}
        </Card>

        <Card className="enterprise-surface-card p-5">
          <p className="inline-flex items-center gap-2 text-sm text-slate-600">
            <Palette className="h-4 w-4 text-violet-600" aria-hidden />
            Branding choices are stored per organization and can be applied by enterprise UI shells.
          </p>
          <p className="enterprise-meta-pill mt-2">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            This is the foundation for future custom domain + branded support surfaces.
          </p>
        </Card>

        <EnterpriseProofPack compact />
      </div>
    </WarmMarketingBackdrop>
  );
}
